import React from 'react'
import PropTypes from 'prop-types'
import {useVickyCMSInjection} from './use-vicky-cms-injection'
import {renderCarouselItems} from '../../../components/optimizely/utils/contentRenderer'
import {Box, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useInView} from '../../../hooks/use-in-view'
import {useHeaderContext} from '../../../contexts/header-context'
import {isHydrated as checkIsHydrated} from '../../../utils/utils'

/**
 * PDP Recommendations Carousel
 * Renders VickyAI recommendation carousels configured in CMS
 * - Lazy loads: only renders carousels when they enter viewport
 * - Each carousel component fetches its own VickyAI data based on AIModel
 * - Only renders carousels configured in CMS (no fallbacks)
 *
 * @param {string} productId - Current product ID/SKU (passed to carousels for similars/bought-together)
 * @param {Object} optimizelyData - Raw Optimizely CMS data
 * @param {boolean} isOptimizelyLoading - Is Optimizely data loading
 * @param {Object} product - Current product data
 */
const PDPRecommendationsCarousel = ({productId, optimizelyData, isOptimizelyLoading, product}) => {
    const {headerHeight} = useHeaderContext()

    // Lazy load: only render carousels when this section enters viewport
    // This delays VickyAI API calls until user scrolls to carousel section
    // Use negative bottom margin to ensure element must be scrolled INTO view, not just near
    const {ref: carouselRef, hasBeenInView} = useInView({
        rootMargin: `-${headerHeight}px 0px 0px 0px`, // Shrink top detection by header height
        triggerOnce: true // Only trigger once, don't refetch on scroll back
    })

    // Get CMS content (no VickyAI fetching - each carousel handles its own)
    const {processedOptimizelyContent} = useVickyCMSInjection(
        productId,
        optimizelyData,
        isOptimizelyLoading
    )

    // Don't render content until after hydration to prevent SSR/client mismatch
    // But always render the outer Box with ref so lazy loading can detect viewport
    if (!checkIsHydrated()) {
        return <Box ref={carouselRef} />
    }

    // Show placeholder while waiting for product or CMS data
    if (!product || !processedOptimizelyContent) {
        return <Box ref={carouselRef} />
    }

    return (
        <Box ref={carouselRef}>
            {/* Only render carousels after section enters viewport (lazy load) */}
            {hasBeenInView && processedOptimizelyContent?.items?.[0]?.MainRegion && (
                <Stack gap={{base: '48px', lg: '64px'}}>
                    {processedOptimizelyContent.items[0].MainRegion.map((item, index) => {
                        const blockContent = item.ContentLink?.Expanded
                        if (!blockContent) return null

                        // Use renderCarouselItems for VickyProductCarouselBlock
                        // Each carousel fetches its own VickyAI data based on AIModel
                        if (blockContent.__typename === 'VickyProductCarouselBlock') {
                            return (
                                <Box key={`pdp-cms-block-${index}`}>
                                    {renderCarouselItems(
                                        blockContent,
                                        `pdp-vicky-${index}`,
                                        productId
                                    )}
                                </Box>
                            )
                        }

                        return null
                    })}
                </Stack>
            )}
        </Box>
    )
}

PDPRecommendationsCarousel.propTypes = {
    productId: PropTypes.string.isRequired,
    optimizelyData: PropTypes.object,
    isOptimizelyLoading: PropTypes.bool,
    product: PropTypes.object
}

export default PDPRecommendationsCarousel
